import { Injectable } from '@angular/core'
import { BaseRouteReuseStrategy, type ActivatedRouteSnapshot } from '@angular/router'

/** Route `data` flag: remount the page when only its params change. */
export const REMOUNT_ON_PARAM_CHANGE = 'remountOnParamChange'

/**
 * FR-087 U4 F1 (#4404) — an OPT-IN remount for record detail pages.
 *
 * Angular reuses a component when only its route params change (`/admin/orders/1` →
 * `/admin/orders/10`). The record detail pages read their id once (ngOnInit / the route
 * snapshot), so a reused page keeps showing — and saving — the previous record. The admin
 * spotlight's records and Recent make that jump an everyday path.
 *
 * A route whose `data.remountOnParamChange === true` is re-created when its params differ;
 * every other route keeps Angular's default (BaseRouteReuseStrategy), so global reuse
 * behaviour is unchanged. Provided once, in app.config.ts (the only place a
 * RouteReuseStrategy can live). Which routes carry the flag is pinned by
 * routes/admin-record-routes-remount.contract.spec.ts.
 */
@Injectable()
export class RemountOnParamChangeStrategy extends BaseRouteReuseStrategy {
  override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    const reuse = super.shouldReuseRoute(future, curr)
    if (
      reuse &&
      future.routeConfig?.data?.[REMOUNT_ON_PARAM_CHANGE] === true &&
      !sameParams(future.params, curr.params)
    ) {
      return false
    }
    return reuse
  }
}

function sameParams(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  return [...keys].every((k) => a[k] === b[k])
}
